import React, { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const DISMISS_KEY = "tsai_pwa_prompt_dismissed_at";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const recentlyDismissed = () => {
  const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return at && Date.now() - at < DISMISS_DAYS * 86400000;
};

export function InstallPWAPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS never fires beforeinstallprompt — show manual instructions instead.
    let t;
    if (isIOS()) {
      t = setTimeout(() => { setIos(true); setShow(true); }, 2500);
    }
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-3 left-3 right-3 sm:left-auto sm:w-[380px] z-[120] rounded-xl border border-[#22D3EE]/40 bg-[#0B1220]/95 backdrop-blur-md shadow-[0_8px_40px_rgba(0,0,0,0.5)] p-4 text-white"
    >
      <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label="Dismiss install prompt"
        className="absolute top-2.5 right-2.5 text-neutral-400 hover:text-white p-1">
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl border border-white/10 shrink-0" />
        <div className="min-w-0">
          <div className="font-bold text-sm">Install TeachingSoftware.ai</div>
          {ios ? (
            <div className="text-xs text-neutral-300 mt-1 leading-relaxed">
              Tap <Share size={12} className="inline -mt-0.5 text-[#22D3EE]" /> <b>Share</b> in Safari, then
              choose <b>"Add to Home Screen"</b> to use it like an app.
            </div>
          ) : (
            <div className="text-xs text-neutral-300 mt-1">
              Get the full-screen app experience — faster access, no browser bars.
            </div>
          )}
          {!ios && (
            <button
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full bg-[#22D3EE] text-[#05070C] hover:bg-[#67E8F9] transition-colors"
            >
              <Download size={13} /> Install app
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstallPWAPrompt;
